import React, { createContext, useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { getFeatureConfig } from '../config/featureRegistry';
import { useSidePanel } from '../contexts/SidePanelContext';
import { useListingData } from './feature/useListingData';
import { useTeamData } from './feature/useTeamData';
import { useFeatureActions } from './feature/useFeatureActions';

export const FeatureContext = createContext(null);

/**
 * Wraps the app (inside <Router>) and provides feature context.
 * On non-feature routes the context value is null.
 */
export function FeatureProvider({ children }) {
  const { pathname } = useLocation();
  const resolved = useMemo(() => getFeatureConfig(pathname), [pathname]);

  if (!resolved) {
    return <FeatureContext.Provider value={null}>{children}</FeatureContext.Provider>;
  }

  return (
    <FeatureContextInternal key={resolved.config.key} resolved={resolved}>
      {children}
    </FeatureContextInternal>
  );
}

// ---------------------------------------------------------------------------
// Internal provider — composed from three focused sub-hooks:
//   useListingData  — listing fetch + local cache
//   useTeamData     — team assignments + normalizeTeams
//   useFeatureActions — CRUD + publish state
// ---------------------------------------------------------------------------

function FeatureContextInternal({ resolved, children }) {
  const { config, context, configId: routeConfigId } = resolved;
  const { setShowSidePanel } = useSidePanel();
  const isListing = context === 'listing';

  // Hide side panel on builder pages (hamburger icon reopens it).
  useEffect(() => {
    if (!isListing) setShowSidePanel(false);
  }, [isListing, setShowSidePanel]);

  // Config ID from URL.
  const configId = routeConfigId;

  // --- Sub-hooks ---
  const listing = useListingData(config, isListing);
  const teamsHook = useTeamData(config);

  const actions = useFeatureActions({
    config,
    isListing,
    listData: listing.data,
    setListData: listing.setData,
    refetchTeams: teamsHook.refetch
  });

  // --- Derived listing rows (adds teamsCount + isDefault + hasThumbnail) ---
  const listData = useMemo(() => {
    if (!listing.data?.length) return listing.data || [];
    return listing.data.map((item) => {
      const itemId = item[config.idKey];
      return {
        ...item,
        teamsCount: teamsHook.teamsCountMapping[itemId]?.length || 0,
        isDefault: Array.isArray(teamsHook.defaultId)
          ? teamsHook.defaultId.includes(itemId)
          : itemId === teamsHook.defaultId,
        hasThumbnail: !!item.thumbnailUri
      };
    });
  }, [listing.data, config.idKey, teamsHook.teamsCountMapping, teamsHook.defaultId]);

  const refresh = useCallback(() => {
    if (isListing) listing.refetch();
    teamsHook.refetch();
  }, [isListing, listing, teamsHook]);

  const value = useMemo(
    () => ({
      // Config
      config,
      context,
      configId,

      // Data
      data: listing.data,
      listData,
      teamAssignments: teamsHook.teamAssignments,
      teamsCountMapping: teamsHook.teamsCountMapping,
      defaultId: teamsHook.defaultId,
      // `loading` covers any state that should block the whole page
      // (list/teams fetch + publish/updateTeams in-flight). Listings wrap
      // their content in ParentWrapper which reads this.
      loading: listing.loading || teamsHook.loading || actions.isPublishLoading,
      listLoading: listing.loading,
      teamsLoading: teamsHook.loading,

      // Publish state (isPublished is canonically boolean)
      publishState: {
        isPublished: actions.isPublished,
        isPublishLoading: actions.isPublishLoading
      },
      setPublishStatus: actions.setIsPublished,

      // Actions
      createItem: actions.createItem,
      deleteItem: actions.deleteItem,
      cloneItem: actions.cloneItem,
      publishItem: actions.publishItem,
      unpublishItem: actions.unpublishItem,
      updateTeams: actions.updateTeams,
      actionsLoading: actions.loading,
      refresh,
      refetchList: listing.refetch,
      refetchTeams: teamsHook.refetch,
      setListData: listing.setData
    }),
    [config, context, configId, listing, listData, teamsHook, actions, refresh]
  );

  return <FeatureContext.Provider value={value}>{children}</FeatureContext.Provider>;
}
